import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 420px;
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0 0 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
`;

const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const TypeCard = styled.button<{ $selected?: boolean }>`
  padding: 16px;
  border: 2px solid ${p => p.$selected ? '#2DBCAF' : '#e2e8f0'};
  border-radius: 12px;
  background: ${p => p.$selected ? 'rgba(45, 188, 175, 0.05)' : 'white'};
  cursor: pointer;
  text-align: center;
  transition: all 0.2s;
  
  &:hover {
    border-color: #2DBCAF;
  }
`;

const TypeName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1a1f2e;
  margin-bottom: 4px;
`;

const TypeDuration = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  &:focus { outline: none; border-color: #2DBCAF; }
`;

const InfoBox = styled.div`
  background: #f8fafc;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 20px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 13px;
  
  span:first-child { color: #64748b; }
  span:last-child { font-weight: 600; color: #1a1f2e; }
`;

const Buttons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button<{ primary?: boolean; danger?: boolean }>`
  flex: 1;
  padding: 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: ${p => p.primary || p.danger ? 'none' : '1px solid #e2e8f0'};
  background: ${p => p.danger ? '#ef4444' : p.primary ? '#2DBCAF' : 'white'};
  color: ${p => p.primary || p.danger ? 'white' : '#64748b'};
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

type PackageType = 'monthly' | 'yearly' | 'fouryear';

const PACKAGE_DAYS: Record<PackageType, number> = {
  monthly: 30,
  yearly: 365,
  fouryear: 1460,
};

interface User {
  _id: string;
  email: string;
  name?: string;
  subscription?: {
    type?: PackageType;
    status?: 'active' | 'expired';
    startDate?: string;
    endDate?: string;
  };
}

interface Props {
  user: User;
  onClose: () => void;
  onSave: () => void;
}

const SubscriptionModal: React.FC<Props> = ({ user, onClose, onSave }) => {
  const [type, setType] = useState<PackageType>('yearly');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState('');
  const [saving, setSaving] = useState(false);

  // Auto-calculate end date
  useEffect(() => {
    const start = new Date(startDate);
    const end = new Date(start);
    end.setDate(end.getDate() + PACKAGE_DAYS[type]);
    setEndDate(end.toISOString().split('T')[0]);
  }, [type, startDate]);

  const handleActivate = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/admin/users/${user._id}/subscription`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ type, startDate, endDate }),
      });
      
      if (res.ok) {
        onSave();
        onClose();
      } else {
        const data = await res.json();
        alert(data.error || 'Gagal mengaktifkan premium');
      }
    } catch {
      alert('Gagal mengaktifkan premium');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!confirm('Nonaktifkan premium untuk user ini?')) return;
    
    setSaving(true);
    try {
      const res = await fetch(`/api/admin/users/${user._id}/subscription`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        onSave();
        onClose();
      } else {
        const data = await res.json();
        alert(data.error || 'Gagal menonaktifkan premium');
      }
    } catch {
      alert('Gagal menonaktifkan premium');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const isActive = user.subscription?.status === 'active';

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Title>Set Premium</Title>
        <Subtitle>{user.email}</Subtitle>

        {/* Current Status */}
        {user.subscription && (
          <InfoBox>
            <InfoRow>
              <span>Status</span>
              <span style={{ color: isActive ? '#16a34a' : '#dc2626' }}>
                {isActive ? '✨ Premium Aktif' : '❌ Expired'}
              </span>
            </InfoRow>
            {user.subscription.type && (
              <InfoRow>
                <span>Paket</span>
                <span>{user.subscription.type === 'fouryear' ? '4 Tahun' : 
                       user.subscription.type === 'yearly' ? 'Setahun' : '1 Bulan'}</span>
              </InfoRow>
            )}
            {user.subscription.endDate && (
              <InfoRow>
                <span>Berakhir</span>
                <span>{formatDate(user.subscription.endDate)}</span>
              </InfoRow>
            )}
          </InfoBox>
        )}

        <FormGroup>
          <Label>Tipe Langganan</Label>
          <TypeGrid style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
            <TypeCard $selected={type === 'monthly'} onClick={() => setType('monthly')}>
              <TypeName>1 Bulan</TypeName>
              <TypeDuration>30 hari</TypeDuration>
            </TypeCard>
            <TypeCard $selected={type === 'yearly'} onClick={() => setType('yearly')}>
              <TypeName>Setahun</TypeName>
              <TypeDuration>365 hari</TypeDuration>
            </TypeCard>
            <TypeCard $selected={type === 'fouryear'} onClick={() => setType('fouryear')}>
              <TypeName>4 Tahun</TypeName>
              <TypeDuration>1460 hari</TypeDuration>
            </TypeCard>
          </TypeGrid>
        </FormGroup>

        <FormGroup>
          <Label>Tanggal Mulai</Label>
          <Input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Tanggal Berakhir</Label>
          <Input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
          />
        </FormGroup>

        <Buttons>
          <Button onClick={onClose}>Batal</Button>
          {isActive && (
            <Button danger onClick={handleDeactivate} disabled={saving}>
              Nonaktifkan
            </Button>
          )}
          <Button primary onClick={handleActivate} disabled={saving}>
            {saving ? 'Menyimpan...' : 'Aktivasi'}
          </Button>
        </Buttons>
      </Modal>
    </Overlay>
  );
};

export default SubscriptionModal;
